/**
 * Displays user role badges with truncation for overflow
 * Shows account role + team memberships
 */
import { MAX_VISIBLE_BADGES, type RoleSummary, type TeamMembership } from "../types";

import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";


interface UserRoleBadgesProps {
	accountRole: RoleSummary;
	teams: TeamMembership[];
}

/**
 * Truncated role badges with tooltip for overflow
 * Shows account role first, then team memberships
 * @param root0 - Component props
 * @param root0.accountRole - The user's account-level role
 * @param root0.teams - Array of team memberships with roles
 */
export function UserRoleBadges({ accountRole, teams }: UserRoleBadgesProps) {
	// Build list of all badges to display
	const allBadges = [
		{ key: "account", label: accountRole.name, type: "account" as const },
		...teams.map((team) => ({
			key: team.id,
			label: `${team.name}: ${team.role.name}`,
			type: "team" as const,
		})),
	];

	if (allBadges.length === 0) {
		return <span className="text-muted-foreground text-sm">No role</span>;
	}

	const visibleBadges = allBadges.slice(0, MAX_VISIBLE_BADGES);
	const hiddenCount = allBadges.length - MAX_VISIBLE_BADGES;
	const hiddenBadges = allBadges.slice(MAX_VISIBLE_BADGES);

	return (
		<div className="flex flex-wrap gap-1">
			{visibleBadges.map((badge) => (
				<Badge
					key={badge.key}
					variant={badge.type === "account" ? "default" : "secondary"}
					className="text-xs"
				>
					{badge.label}
				</Badge>
			))}
			{hiddenCount > 0 && (
				<Popover>
					<PopoverTrigger asChild>
						<Badge
							variant="outline"
							className="text-xs cursor-pointer hover:bg-accent"
						>
							+{hiddenCount} more
						</Badge>
					</PopoverTrigger>
					<PopoverContent className="w-auto p-2" align="start">
						<div className="flex flex-col gap-1">
							{hiddenBadges.map((badge) => (
								<Badge
									key={badge.key}
									variant={badge.type === "account" ? "default" : "secondary"}
									className="text-xs"
								>
									{badge.label}
								</Badge>
							))}
						</div>
					</PopoverContent>
				</Popover>
			)}
		</div>
	);
}
